import { BEIGE_300, BEIGE_600, BLUE_100, BLUE_600, GRAY_400, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { EditionType, getEditionTypeString, useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { MANUAL_EDIT_TOOLS, getManualToolIconRedesigned, toolToPinturaFeatureMapRedesigned } from '../../PhotoStudioUtils'

import { AdjustLockedPanel } from '../adjust/AdjustLockedPanel.component'
import { AdjustToolsPanel } from '../adjust/AdjustToolsPanel.component'
import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { FC } from 'react'
import { NonSubscriberAdjustUpsell } from './NonSubscriberAdjustUpsell.component'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

interface AdjustPanelProps {
  /**
   * Inside the Adjust section of "Display all features at once": no own padding, tools on two columns, and picking a
   * tool first puts Pintura on the canvas (`beforeToolSelect`).
   */
  embedded?: boolean
  beforeToolSelect?: () => Promise<void>
}

export const AdjustPanel: FC<AdjustPanelProps> = ({ embedded = false, beforeToolSelect }) => {
  const { t } = useTranslation(['photo_studio_editing'])

  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  const {
    setSelectedPinturaFeature,
    selectedPinturaFeature,
    isProcessingImage,
    isOriginalVisualType,
    logPhotoStudioEvent,
    getCommonAnalyticsData,
    sourceVisualFilename,
    isPinturaReplacement,
    isLabSdkEngine,
  } = useClientPhotoStudio()

  const isToolDisabled = isProcessingImage || isOriginalVisualType
  // Lab engine without a subscription: the tool list, locked, like the other Lab tools (instead of the upsell)
  const isLabLocked = isLabSdkEngine && !hasWorkspaceActiveSubscription

  return (
    <>

      {isLabLocked && <AdjustLockedPanel embedded={embedded} />}

      {/* In-house editor (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT): its own panel, in both views */}
      {isPinturaReplacement && !isLabLocked && <AdjustToolsPanel embedded={embedded} beforeToolSelect={beforeToolSelect} />}

      {/* SUBSCRIBERS ONLY SECTION */}
      {!isPinturaReplacement && !hasWorkspaceActiveSubscription && !isLabLocked && (
        <NonSubscriberAdjustUpsell />
      )}

      {!isPinturaReplacement && hasWorkspaceActiveSubscription &&
        <Stack
          justifyContent="flex-start"
          alignItems="stretch"
          spacing=".8rem"
          padding={embedded ? 0 : 0.8}
          width="100%"
          sx={{
            cursor: isOriginalVisualType ? 'not-allowed' : 'default',
          }}
        >
          {/* Info panel – same style as Magic Eraser instructions box */}
          <BorderBoxWrapper
            gap={0.2}
            padding={1.2}
            borderColor={BEIGE_600}
            backgroundColor={BEIGE_300}
            sx={{
              borderRadius: '0.8rem',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Stack gap={0.2} sx={{ width: '100%', maxWidth: '100%' }}>
              <Typography
                variant="text-sm"
                fontWeight={550}
                color={GRAY_900}
                sx={{
                  fontSize: '1.4rem',
                  lineHeight: '1.4',
                  letterSpacing: '-0.56px',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  width: '100%',
                }}
              >
                {t('adjust_instructions.title')}
              </Typography>
              <Typography
                variant="text-sm"
                fontWeight={350}
                color={GRAY_900}
                sx={{
                  fontSize: '1.4rem',
                  lineHeight: '1.4',
                  letterSpacing: '-0.56px',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  width: '100%',
                }}
              >
                {t('adjust_instructions.description')}
              </Typography>
            </Stack>
          </BorderBoxWrapper>

          <Box sx={{ display: 'grid', gridTemplateColumns: embedded ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)', gap: '.8rem' }}>
            {Object.values(MANUAL_EDIT_TOOLS).map((tool) => {
              const isSelectedTool =
                String(selectedPinturaFeature).toLowerCase() ===
                String(toolToPinturaFeatureMapRedesigned[tool]).toLowerCase()

              return (
                <Box
                  key={tool}
                  onClick={async () => {
                    if (!isProcessingImage) {
                      await beforeToolSelect?.()
                      const newPinturaFeature = toolToPinturaFeatureMapRedesigned[tool]
                      // Then set the specific Pintura tool
                      setSelectedPinturaFeature(newPinturaFeature)

                      // Track edition selected
                      if (logPhotoStudioEvent && getCommonAnalyticsData) {
                        getCommonAnalyticsData().then(commonData => {
                          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
                            editionType: getEditionTypeString(EditionType.ADJUST, newPinturaFeature),
                            editionSettings: {
                              pinturaFeature: newPinturaFeature,
                            },
                            ...commonData,
                          })
                        }).catch(() => {
                          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
                            editionType: getEditionTypeString(EditionType.ADJUST, newPinturaFeature),
                            editionSettings: {
                              pinturaFeature: newPinturaFeature,
                            },
                            sourceVisualFilename,
                          })
                        })
                      }
                    }
                  }}
                  sx={{
                    backgroundColor: isSelectedTool ? BLUE_100 : WHITE,
                    height: '4.4rem',
                    gap: '.8rem',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '1.2rem',
                    borderRadius: '.8rem',
                    border: '1px solid',
                    borderColor: isSelectedTool ? BLUE_600 : GRAY_400,
                    color: isSelectedTool ? GRAY_900 : GRAY_700,
                    textAlign: 'center',
                    fontWeight: isSelectedTool ? '600' : '400',
                    pointerEvents: isToolDisabled ? 'none' : 'auto',
                    cursor: isToolDisabled ? 'not-allowed' : 'pointer',
                    opacity: isToolDisabled ? 0.5 : 1,
                    '&:hover': {
                      backgroundColor: isToolDisabled ? (isSelectedTool ? BLUE_100 : WHITE) : BLUE_100,
                      borderColor: isToolDisabled ? (isSelectedTool ? BLUE_600 : GRAY_400) : BLUE_600,
                      fontWeight: isToolDisabled ? (isSelectedTool ? '600' : '400') : '600',
                      color: isToolDisabled ? (isSelectedTool ? GRAY_900 : GRAY_700) : GRAY_900,
                    },
                    '&:hover .tool-label': {
                      color: isToolDisabled ? (isSelectedTool ? GRAY_900 : GRAY_700) : GRAY_900,
                    },
                  }}
                >
                  {getManualToolIconRedesigned(tool)}
                  <Typography
                    className="tool-label"
                    variant="text-sm"
                    color={isSelectedTool ? GRAY_900 : GRAY_700}
                    fontWeight={450}
                  >
                    {t(`manual_tools.${tool}`)}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Stack>
      }

    </>
  )
}
